import { useT } from "../../../core/i18n";
import { DatePicker } from "antd";
import moment from "moment";
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import CommonSelect from "../../../core/common/commonSelect";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Toasts from "../../../core/common/toast";
import {
  getEmployeeProfile,
  getMyProfile,
  updateEmergencyContact,
  updateEmployeeAddress,
  updateEmployeeContact,
  verifyEmployeeContact,
  getProfilePictureHistory,
  updateProfilePicture,
  getPoliceVerificationStatus,
} from "../../../core/data/redux/employeesSlice";
import { getDependencyTypeDescription } from "../../../utils/dependencyTypeMapping";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
type PasswordField = "password" | "confirmPassword";

const DEFAULT_INDIAN_STATES: Array<{ region_id: string; region_name: string }> = [
  { region_id: "01", region_name: "Andhra Pradesh" },
  { region_id: "02", region_name: "Arunachal Pradesh" },
  { region_id: "03", region_name: "Assam" },
  { region_id: "04", region_name: "Bihar" },
  { region_id: "05", region_name: "Chhattisgarh" },
  { region_id: "06", region_name: "Goa" },
  { region_id: "07", region_name: "Gujarat" },
  { region_id: "08", region_name: "Haryana" },
  { region_id: "09", region_name: "Himachal Pradesh" },
  { region_id: "10", region_name: "Jharkhand" },
  { region_id: "11", region_name: "Karnataka" },
  { region_id: "12", region_name: "Kerala" },
  { region_id: "13", region_name: "Madhya Pradesh" },
  { region_id: "14", region_name: "Maharashtra" },
  { region_id: "15", region_name: "Manipur" },
  { region_id: "16", region_name: "Meghalaya" },
  { region_id: "17", region_name: "Mizoram" },
  { region_id: "18", region_name: "Nagaland" },
  { region_id: "19", region_name: "Odisha" },
  { region_id: "20", region_name: "Punjab" },
  { region_id: "21", region_name: "Rajasthan" },
  { region_id: "22", region_name: "Sikkim" },
  { region_id: "23", region_name: "Tamil Nadu" },
  { region_id: "24", region_name: "Telangana" },
  { region_id: "25", region_name: "Tripura" },
  { region_id: "26", region_name: "Uttar Pradesh" },
  { region_id: "27", region_name: "Uttarakhand" },
  { region_id: "28", region_name: "West Bengal" },
  { region_id: "DL", region_name: "Delhi" },
  { region_id: "CH", region_name: "Chandigarh" },
  { region_id: "JK", region_name: "Jammu and Kashmir" },
  { region_id: "LA", region_name: "Ladakh" },
  { region_id: "PY", region_name: "Puducherry" },
  { region_id: "AN", region_name: "Andaman and Nicobar Islands" },
  { region_id: "DN", region_name: "Dadra and Nagar Haveli and Daman and Diu" },
  { region_id: "LD", region_name: "Lakshadweep" },
];

const MyProfile = () => {
  const { t } = useT();
  const dispatch = useDispatch();
  const employeesState = useSelector((state: any) => state.employeesState);

  const fetchRecords = () => {
    dispatch(getMyProfile({}));
  };

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    relation: "",
    address: "",
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [otpArray, setOtpArray] = useState<string[]>(Array(6).fill(""));

  const handleOtpChange = (value: string, index: number) => {
    const alphanumeric = /^[a-zA-Z0-9]$/;

    if (value && alphanumeric.test(value)) {
      const upperCaseValue = value.toUpperCase();

      const newOtpArray = [...otpArray];
      newOtpArray[index] = upperCaseValue;
      setOtpArray(newOtpArray);

      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === "") {
      const newOtpArray = [...otpArray];
      newOtpArray[index] = "";
      setOtpArray(newOtpArray);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let { name, value } = e.target;
    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }
    if (!form.phone || form.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      // Call API
      await dispatch(updateEmergencyContact({ ...form })).unwrap();
      dispatch(getMyProfile({}));

      // ✅ Reset form after successful submit
      setForm({
        name: "",
        phone: "",
        email: "",
        relation: "",
        address: "",
      });

      // ✅ Close modal
      const modalEl = document.getElementById("edit_emergency");
      if (modalEl) {
        modalEl.classList.remove("show");
        modalEl.setAttribute("aria-hidden", "true");
        (document.querySelector(".modal-backdrop") as HTMLElement)?.remove();
        document.body.classList.remove("modal-open");
      }
    } catch (error) {
      console.error("Update or fetch failed", error);
    }
  };

  const fetchEmployeeRecords = (empId: string) => {
    dispatch(getEmployeeProfile({ id: empId }));
  };

  const updateAddressData = (data: any) => {
    dispatch(updateEmployeeAddress(data));
  };

  const handleUpdateContact = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length !== 10) {
      alert("Please enter a valid 10-digit contact number");
      return;
    }
    console.log("Updating contact...");

    try {
      // Call API with action for profile update
      dispatch(updateEmployeeContact({
        phone_number: phoneNumber,
        action: "PROFILE_UPDATE"
      })).unwrap();
      // dispatch(getMyProfile({}));

      // // ✅ Close modal
      // const modalEl = document.getElementById("edit_contact");
      // if (modalEl) {
      //   modalEl.classList.remove("show");
      //   modalEl.setAttribute("aria-hidden", "true");
      //   (document.querySelector(".modal-backdrop") as HTMLElement)?.remove();
      //   document.body.classList.remove("modal-open");
      // }
    } catch (error) {
      console.error("Update or fetch failed", error);
    }
  };

  const handleVerifyOTP = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const otp = otpArray.join("");
    console.log("Phone Number:", phoneNumber);
    try {
      dispatch(verifyEmployeeContact({
        phone_number: phoneNumber,
        otp,
        action: "PROFILE_UPDATE"
      }));
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  let user = employeesState?.data?.data;
  let controllingOfficer = employeesState?.controllingOfficerData?.data;
  let updateAddressMsg = employeesState?.updateAddressData?.message;
  let updateContactMsg = employeesState?.updateContactData?.message;
  let updateContactStatus = employeesState?.updateContactData?.status;
  let verifyContactMsg = employeesState?.verifyContactData?.message;
  let verifyContactStatus = employeesState?.verifyContactData?.status;
  let error = employeesState?.error;

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    if (user && user?.details?.phone) setPhoneNumber(user.details.phone);
  }, [user]);

  useEffect(() => {
    if (verifyContactStatus === 201) {
      dispatch(getMyProfile({}));
      // ✅ Close modal
      const modalEl = document.getElementById("edit_contact");
      if (modalEl) {
        modalEl.classList.remove("show");
        modalEl.setAttribute("aria-hidden", "true");
        (document.querySelector(".modal-backdrop") as HTMLElement)?.remove();
        document.body.classList.remove("modal-open");
      }
    }
  }, [verifyContactStatus]);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPreviousRequests, setShowPreviousRequests] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showDisplayPhoto, setShowDisplayPhoto] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedPhotoPreview, setUploadedPhotoPreview] = useState<string>("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [editAddressData, setEditAddressData] = useState<any>({});
  const [showTableSection, setShowTableSection] = useState(false);
  const [showTempAddressModal, setShowTempAddressModal] = useState(false);
  const [addressTypes, setAddressTypes] = useState<
    Array<{ id?: number; type_id: string; name: string }>
  >([]);
  const [regions, setRegions] = useState<
    Array<{ region_id?: string; region_name: string }>
  >([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [addressErrors, setAddressErrors] = useState<{ [key: string]: string }>({});
  const [tempAddressForm, setTempAddressForm] = useState({
    addressType: "",
    subtype: "",
    country: "India",
    careOf: "",
    houseNumber: "",
    street: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    telephone: "",
    validOption: "today", // 'today' | 'from' | 'fromTo'
    validFrom: new Date().toISOString().split("T")[0],
    validTo: "",
  });

  useEffect(() => {
    if (showTempAddressModal) {
      const fetchTypes = async () => {
        try {
          const res = await Api.post(API_URLS.ROLE_PERMISSIONS.FIND_ALL_ADDRESS_TYPES, {});
          const list = res?.data?.data?.data || res?.data?.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setAddressTypes(list);
          } else {
            setAddressTypes([
              { type_id: "1", name: "Permanent Residence" },
              { type_id: "2", name: "Temporary Residence" },
              { type_id: "3", name: "Home Address" },
              { type_id: "4", name: "Emergency Address" },
              { type_id: "5", name: "Present Address" },
            ]);
          }
        } catch (e) {
          console.error("Failed to fetch address types:", e);
          setAddressTypes([
            { type_id: "1", name: "Permanent Residence" },
            { type_id: "2", name: "Temporary Residence" },
            { type_id: "3", name: "Home Address" },
            { type_id: "4", name: "Emergency Address" },
            { type_id: "5", name: "Present Address" },
          ]);
        }
      };

      const fetchRegions = async () => {
        setLoadingRegions(true);
        try {
          const res = await Api.post(
            API_URLS.ROLE_PERMISSIONS.FIND_ALL_REGIONS_SAP,
            { limit: 100 }
          );
          const list = res?.data?.data?.data || res?.data?.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setRegions(list);
          } else {
            setRegions(DEFAULT_INDIAN_STATES);
          }
        } catch (e) {
          console.error("Failed to fetch regions:", e);
          setRegions(DEFAULT_INDIAN_STATES);
        } finally {
          setLoadingRegions(false);
        }
      };

      fetchTypes();
      fetchRegions();
    }
  }, [showTempAddressModal]);

  const availableAddressTypes = useMemo(() => {
    const existingTypeNames = (user?.addresses ?? []).map((a: any) =>
      (a.addressType || "").toLowerCase().trim()
    );
    const available = addressTypes.filter(
      (t) => !existingTypeNames.includes((t.name || "").toLowerCase().trim())
    );
    return available.length > 0 ? available : addressTypes;
  }, [addressTypes, user?.addresses]);

  const [previousRequests, setPreviousRequests] = useState<any[]>([]);

  const fetchProfileHistory = async () => {
    try {
      const payload = { empId: user?.empId };
      const res = await dispatch(getProfilePictureHistory(payload)).unwrap();
      if (res?.success && res?.data) {
        setPreviousRequests(res.data);
      }
    } catch (error) {
      console.error("Error fetching profile history", error);
    }
  };

  useEffect(() => {
    if (user?.empId) {
      fetchProfileHistory();
    }
  }, [user?.empId]);

  useEffect(() => {
    if (employeesState?.data?.data) {
      const freshData = employeesState.data.data;
      try {
        const stored = localStorage.getItem("user");
        const parsed = stored && stored !== "undefined" ? JSON.parse(stored) : {};
        const updated = {
          ...parsed,
          ...freshData,
          details: { ...parsed?.details, ...freshData?.details },
          sap_employee_images: freshData?.sap_employee_images || parsed?.sap_employee_images,
          image: freshData?.image || freshData?.details?.image || parsed?.image,
        };
        localStorage.setItem("user", JSON.stringify(updated));
      } catch (err) {
        console.error("Failed to update user in localStorage", err);
      }
    }
  }, [employeesState?.data]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ["image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        alert("Please upload only JPG or JPEG files.");
        return;
      }

      const fileSizeInKB = file.size / 1024;
      if (fileSizeInKB < 20 || fileSizeInKB > 100) {
        alert("File size must be between 20KB and 100KB.");
        return;
      }

      setSelectedFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPhoto = async () => {
    if (selectedFile) {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        reader.onload = async () => {
          try {
            const base64String = (reader.result as string).split(",")[1];
            const fileName = selectedFile.name;
            const fileExtension = fileName.split(".").pop()?.toLowerCase();
            const fileSize = `${Math.round(selectedFile.size / 1024)}KB`;

            const payload = {
              FileData: base64String,
              FileName: fileName,
              DocumentID: "5E2C6BD0725C1FE0A4DE2DA92D701617",
              FileExtension: fileExtension,
              FileSize: fileSize,
            };

            const res = await dispatch(updateProfilePicture(payload)).unwrap();
            if (res?.success) {
              fetchProfileHistory();
              fetchRecords();
              setShowNewRequest(false);
              setSelectedFile(null);
              setUploadedPhotoPreview("");
              alert("File uploaded successfully");
            } else {
              alert(res?.message || "Upload failed");
            }
          } catch (error: any) {
            console.error("Upload error", error);
            alert(error?.message || error || "An error occurred during upload");
          }
        };
      } catch (error) {
        console.error("Upload error", error);
        alert("An error occurred during upload");
      }
    }
  };

  const resetTempAddressForm = () => {
    setTempAddressForm({
      addressType: "",
      subtype: "",
      country: "India",
      careOf: "",
      houseNumber: "",
      street: "",
      address: "",
      city: "",
      state: "",
      postalCode: "",
      telephone: "",
      validOption: "today",
      validFrom: new Date().toISOString().split("T")[0],
      validTo: "",
    });
    setAddressErrors({});
  };

  const handleTempAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let { name, value } = e.target;
    if (name === "postalCode") {
      value = value.replace(/\D/g, "").slice(0, 6);
    }
    if (name === "telephone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "addressType") {
      const selectedType = addressTypes.find((t) => t.name === value);
      setTempAddressForm((prev) => ({
        ...prev,
        addressType: value,
        subtype: selectedType?.type_id || "",
      }));
      setAddressErrors((prev) => ({ ...prev, addressType: "" }));
      return;
    }
    setTempAddressForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setAddressErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleValidOptionChange = (option: string) => {
    setTempAddressForm((prev) => ({
      ...prev,
      validOption: option,
    }));
  };

  const validateAddressForm = () => {
    const errs: { [key: string]: string } = {};
    if (!tempAddressForm.addressType) {
      errs.addressType = "Address Type is required";
    }
    if (!tempAddressForm.country.trim()) {
      errs.country = "Country is required";
    }
    if (!tempAddressForm.careOf.trim()) {
      errs.careOf = "Care Of is required";
    }
    if (!tempAddressForm.houseNumber.trim()) {
      errs.houseNumber = "House Number is required";
    }
    if (!tempAddressForm.street.trim()) {
      errs.street = "Street is required";
    }
    if (!tempAddressForm.address.trim()) {
      errs.address = "Address is required";
    }
    if (!tempAddressForm.city.trim()) {
      errs.city = "City is required";
    }
    if (!tempAddressForm.state.trim()) {
      errs.state = "State is required";
    }
    if (!tempAddressForm.postalCode.trim()) {
      errs.postalCode = "Postal Code is required";
    } else if (!/^\d{6}$/.test(tempAddressForm.postalCode.trim())) {
      errs.postalCode = "Postal Code must be exactly 6 digits";
    }
    if (!tempAddressForm.telephone.trim()) {
      errs.telephone = "Telephone is required";
    } else if (!/^\d{10}$/.test(tempAddressForm.telephone.trim())) {
      errs.telephone = "Telephone must be exactly 10 digits";
    }
    if (tempAddressForm.validOption === "from" && !tempAddressForm.validFrom) {
      errs.validFrom = "From Date is required";
    }
    if (tempAddressForm.validOption === "fromTo") {
      if (!tempAddressForm.validFrom) {
        errs.validFrom = "From Date is required";
      }
      if (!tempAddressForm.validTo) {
        errs.validTo = "To Date is required";
      }
    }
    setAddressErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleTempAddressSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateAddressForm()) {
      return;
    }
    try {
      const payload: any = {
        careOf: tempAddressForm.careOf,
        house_number: tempAddressForm.houseNumber,
        streetHouse: tempAddressForm.street,
        address_line_2: tempAddressForm.address,
        city: tempAddressForm.city,
        district: tempAddressForm.state,
        postalCode: tempAddressForm.postalCode,
        country: tempAddressForm.country,
        telephoneNo: tempAddressForm.telephone,
        subtype: tempAddressForm.subtype,
        addressType: tempAddressForm.addressType,
      };
      const res = await dispatch(updateEmployeeAddress(payload)).unwrap();
      alert(res?.message || "Address added successfully");
      setShowTempAddressModal(false);
      resetTempAddressForm();
      dispatch(getMyProfile({}));
    } catch (err: any) {
      alert(typeof err === "string" ? err : err?.message || "Failed to add address");
    }
  };

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility(
      (prevState: { password: boolean; confirmPassword: boolean }) => ({
        ...prevState,
        [field]: !prevState[field],
      })
    );
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  const departmentChoose = [
    { value: "Select", label: "Select" },
    { value: "All Department", label: "All Department" },
    { value: "Finance", label: "Finance" },
    { value: "Developer", label: "Developer" },
    { value: "Executive", label: "Executive" },
  ];
  const designationChoose = [
    { value: "Select", label: "Select" },
    { value: "Finance", label: "Finance" },
    { value: "Developer", label: "Developer" },
    { value: "Executive", label: "Executive" },
  ];
  const martialstatus = [
    { value: "Select", label: "Select" },
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];
  const salaryChoose = [
    { value: "Select", label: "Select" },
    { value: "Monthly", label: "Monthly" },
    { value: "Annualy", label: "Annualy" },
  ];
  const paymenttype = [
    { value: "Select", label: "Select" },
    { value: "Cash", label: "Cash" },
    { value: "Debit Card", label: "Debit Card" },
    { value: "Mobile Payment", label: "Mobile Payment" },
  ];
  const pfcontribution = [
    { value: "Select", label: "Select" },
    { value: "Employee Contribution", label: "Employee Contribution" },
    { value: "Employer Contribution", label: "Employer Contribution" },
    { value: "Provident Fund Interest", label: "Provident Fund Interest" },
  ];
  const additionalrate = [
    { value: "Select", label: "Select" },
    { value: "ESI", label: "ESI" },
    { value: "EPS", label: "EPS" },
    { value: "EPF", label: "EPF" },
  ];
  const esi = [
    { value: "Select", label: "Select" },
    { value: "Employee Contribution", label: "Employee Contribution" },
    { value: "Employer Contribution", label: "Employer Contribution" },
    { value: "Maternity Benefit ", label: "Maternity Benefit " },
  ];
  const addressType = [
    { value: "Select", label: "Select" },
    { value: "Permanent residence", label: "Permanent residence" },
    { value: "Current residence", label: "Current residence" },
    { value: "Home residence", label: "Home residence" },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        {updateAddressMsg && (
          <Toasts
            show={updateAddressMsg}
            message={updateAddressMsg}
            title={"Success"}
            type={"success"}
          />
        )}
        {verifyContactMsg && (
          <Toasts
            show={verifyContactMsg}
            message={verifyContactMsg}
            title={"Success"}
            type={"success"}
          />
        )}
        {updateContactMsg && (
          <Toasts
            show={updateContactMsg}
            message={updateContactMsg}
            title={"Success"}
            type={"success"}
          />
        )}
        {error ? (
          <Toasts
            show={error}
            message={error}
            title={"Error"}
            type={"danger"}
          />
        ) : null}
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h6 className="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
                <Link to={all_routes.myProfile}>
                  <i className="ti ti-arrow-left me-2" />
                  Employee Details
                </Link>
              </h6>
            </div>
            {/* <div className="">{user?.empId}</div> */}

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <Link
                  to="#"
                  data-bs-toggle={
                    user?.details?.manager ||
                      user?.details?.reportingManagerName
                      ? "modal"
                      : ""
                  }
                  data-inert={true}
                  data-bs-target={
                    user?.details?.manager ||
                      user?.details?.reportingManagerName
                      ? "#know_your_co"
                      : ""
                  }
                  className={`btn d-flex align-items-center ${user?.details?.manager ||
                    user?.details?.reportingManagerName
                    ? "btn-primary"
                    : "btn-secondary disabled"
                    }`}
                  title={
                    user?.details?.manager ||
                      user?.details?.reportingManagerName
                      ? "View controlling officer details"
                      : "No controlling officer assigned"
                  }
                  style={
                    user?.details?.manager ||
                      user?.details?.reportingManagerName
                      ? {}
                      : { cursor: "not-allowed", pointerEvents: "none" }
                  }
                >
                  <i className="ti ti-circle-plus me-2" />
                  Know Your CO
                </Link>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            <div className="col-xl-4 theiaStickySidebar">
              <div className="card card-bg-1">
                <div className="card-body p-0">
                  <div className="avatar-container position-relative d-flex justify-content-center mb-2">
                    <span
                      className="avatar avatar-xl avatar-rounded border border-2 border-white position-relative"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => {
                        const editIcon = e.currentTarget.querySelector(
                          ".edit-profile-icon"
                        ) as HTMLElement;
                        if (editIcon) editIcon.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        const editIcon = e.currentTarget.querySelector(
                          ".edit-profile-icon"
                        ) as HTMLElement;
                        if (editIcon) editIcon.style.opacity = "0";
                      }}
                      onClick={() => setShowNewRequest(true)}
                    >
                      <img
                        alt="Profile Pic"
                        src={
                          user?.details?.image || user?.sap_employee_images?.image || user?.image
                            ? (user?.details?.image || user?.sap_employee_images?.image || user?.image).startsWith("data:") ||
                              (user?.details?.image || user?.sap_employee_images?.image || user?.image).startsWith("http")
                              ? (user?.details?.image || user?.sap_employee_images?.image || user?.image)
                              : `data:image/jpeg;base64,${user?.details?.image || user?.sap_employee_images?.image || user?.image}`
                            : `assets/img/profiles/place.jpg`
                        }
                        className="w-100 h-100 rounded-circle object-fit-cover"
                      />
                      <div
                        className="edit-profile-icon position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          bottom: "0",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "100%",
                          height: "30px",
                          background: "rgba(0,0,0,0.6)",
                          borderBottomLeftRadius: "50%",
                          borderBottomRightRadius: "50%",
                          opacity: "0",
                          transition: "opacity 0.3s ease",
                          zIndex: 2,
                        }}
                        title="Edit Profile Picture"
                      >
                        <i
                          className="ti ti-camera text-white"
                          style={{ fontSize: "16px" }}
                        ></i>
                      </div>
                    </span>
                  </div>
                  {/* <pre>{JSON.stringify(user, null, 2)}</pre> */}
                  <div className="text-center px-3 pb-3 border-bottom">
                    <div className="mb-3">
                      <h5 className="d-flex align-items-center justify-content-center mb-1 text-uppercase">
                        {user?.details?.firstname} {user?.details?.lsastname}
                        <i className="ti ti-discount-check-filled text-success ms-1" />
                      </h5>
                    </div>
                    <div>
                      {/* <pre>{JSON.stringify(user?.details, null, 2)}</pre> */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Date of Birth
                        </span>
                        <p className="text-dark">
                          {user?.details?.birthdate
                            ? moment(user?.details?.birthdate).format(
                              "DD-MM-YYYY"
                            )
                            : ""}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Marital Status
                        </span>
                        <p className="text-dark text-uppercase ">
                          {user?.details?.marital_status || "N/A"}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Pan Number
                        </span>
                        <p className="text-dark text-uppercase">
                          {user?.details?.panNumber || "N/A"}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Aadhaar Number
                        </span>
                        <p className="text-dark text-uppercase">
                          {user?.details?.aadhaarId &&
                            user.details.aadhaarId.trim()
                            ? "X".repeat(user.details.aadhaarId.length - 4) +
                            user.details.aadhaarId.slice(-4)
                            : "N/A"}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          email
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <p className="text-dark text-uppercase mb-0">
                            {user?.details?.email}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Designation
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <p className="text-dark text-uppercase mb-0">
                            {user?.details?.sap_department_designation_wings
                              ?.designation_name || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Department
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <p className="text-dark text-uppercase mb-0">
                            {user?.details?.sap_department_designation_wings
                              ?.department_name || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Wing
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <p className="text-dark text-uppercase mb-0">
                            {user?.details?.sap_department_designation_wings
                              ?.wing || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Blood Group
                        </span>
                        <p className="text-dark text-uppercase">
                          {user?.details?.blood_group}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Contact No.
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <p className="text-dark text-uppercase mb-0">
                            {user?.details?.phone}
                          </p>
                          <i
                            className="ti ti-edit"
                            data-bs-toggle="modal"
                            data-inert={true}
                            data-bs-target="#edit_contact"
                          />
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Date of Joining
                        </span>
                        <p className="text-dark text-uppercase">
                          {user?.details?.date_of_joining
                            ? moment(user?.details?.date_of_joining).format(
                              "DD-MM-YYYY"
                            )
                            : "N/A"}
                        </p>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="d-inline-flex align-items-center text-uppercase">
                          Wing
                        </span>
                        <p className="text-dark text-uppercase">
                          {user?.details?.sap_department_designation_wings
                            ?.wing_name || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-8">
              <div>
                <div className="tab-content custom-accordion-items">
                  <div
                    className="tab-pane active show"
                    id="bottom-justified-tab1"
                    role="tabpanel"
                  >
                    <div
                      className="accordion accordions-items-seperate"
                      id="accordionExample"
                    >
                      {/* Emergency Contact */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingOne">
                          <div className="accordion-button">
                            <div className="d-flex align-items-center flex-fill">
                              <h5 className="text-uppercase">
                                Emergency Contact Details
                              </h5>
                              <Link
                                to="#"
                                className="btn btn-sm btn-icon ms-auto"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#edit_emergency"
                              >
                                <i className="ti ti-edit" />
                              </Link>
                              <Link
                                to="#"
                                className="d-flex align-items-center collapsed collapse-arrow"
                                data-bs-toggle="collapse"
                                data-bs-target="#primaryBorderOne"
                                aria-expanded="false"
                                aria-controls="primaryBorderOne"
                              >
                                <i className="ti ti-chevron-down fs-18" />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderOne"
                          className="accordion-collapse collapse show border-top accordion-body"
                          aria-labelledby="headingOne"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="d-flex flex-column gap-2">
                            {user?.emergency_contacts?.length > 0
                              ? user.emergency_contacts.map(
                                (contact: any, index: number) => (
                                  <div
                                    className="d-flex flex-column gap-1"
                                    key={index}
                                  >
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Name:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{contact.name}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Phone Number:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{contact.phone}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Relation:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{contact.relation}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Email ID:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{contact.email}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Address:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{contact.address}</span>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )
                              : null}
                          </div>
                        </div>
                      </div>
                      {/* Address */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingTwo">
                          <div className="accordion-button">
                            <div className="d-flex align-items-center justify-content-between flex-fill">
                              <h5 className="text-uppercase">ADDRESS</h5>
                              <Link
                                to="#"
                                className="btn btn-sm btn-primary btn-icon ms-auto me-2"
                                onClick={() => setShowTempAddressModal(true)}
                                title="Add Address"
                              >
                                <i className="ti ti-plus" />
                              </Link>
                              <Link
                                to="#"
                                className="d-flex align-items-center collapsed collapse-arrow"
                                data-bs-toggle="collapse"
                                data-bs-target="#primaryBorderTwo"
                                aria-expanded="false"
                                aria-controls="primaryBorderTwo"
                              >
                                <i className="ti ti-chevron-down fs-18" />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderTwo"
                          className="accordion-collapse collapse border-top accordion-body"
                          aria-labelledby="headingTwo"
                          data-bs-parent="#accordionExample"
                        >
                          {user?.addresses?.length > 0
                            ? user.addresses.map(
                              (address: any, index: number) => (
                                <div key={index}>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline mb-2">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      {address.addressType}
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>
                                        {[
                                          address?.streetHouse,
                                          address?.address_line_2,
                                          address?.city,
                                          address?.postalCode,
                                          address?.district,
                                          address?.country,
                                        ]
                                          .filter(Boolean)
                                          .join(", ")}
                                      </span>
                                      {/* <Link
                                          to="#"
                                          data-bs-toggle="modal"
                                          data-inert={true}
                                          data-bs-target="#edit_address"
                                          onClick={() =>
                                            setEditAddressData(address)
                                          }
                                        >
                                          <i className="ti ti-edit" />
                                        </Link> */}
                                    </div>
                                  </div>
                                  {index < user.addresses.length - 1 && (
                                    <hr
                                      style={{
                                        margin: "12px 0",
                                        border: "1px solid #ccc",
                                        backgroundColor: "#ccc",
                                      }}
                                    />
                                  )}
                                </div>
                              )
                            )
                            : null}
                        </div>
                      </div>
                      {/* Bank Information */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingThree">
                          <div className="accordion-button">
                            <div className="d-flex align-items-center justify-content-between flex-fill">
                              <h5 className="text-uppercase">
                                Bank Information
                              </h5>
                              {/* <Link
                                to="#"
                                className="btn btn-sm btn-icon ms-auto"
                                data-bs-toggle="modal"
                                data-inert={true}
                                data-bs-target="#edit_bank"
                              >
                                <i className="ti ti-edit" />
                              </Link> */}
                              <Link
                                to="#"
                                className="d-flex align-items-center collapsed collapse-arrow"
                                data-bs-toggle="collapse"
                                data-bs-target="#primaryBorderThree"
                                aria-expanded="false"
                                aria-controls="primaryBorderThree"
                              >
                                <i className="ti ti-chevron-down fs-18" />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderThree"
                          className="accordion-collapse collapse border-top accordion-body"
                          aria-labelledby="headingThree"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="d-flex flex-column gap-2">
                            {user?.banks?.length > 0
                              ? user.banks.map((bank: any, index: number) => (
                                <div
                                  className="d-flex flex-column gap-1"
                                  key={index}
                                >
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >{t("payee_name")}</div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{bank.bank_payee}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >{t("bank_name")}</div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{bank.bank_name}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      A/C no:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{bank.bank_account}</span>
                                    </div>
                                  </div>
                                  {/* <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        IFSC Code:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{bank.bank_key}</span>
                                      </div>
                                    </div> */}
                                </div>
                              ))
                              : null}
                          </div>
                        </div>
                      </div>
                      {/* Family Information */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingFour">
                          <div className="accordion-button">
                            <div className="d-flex align-items-center justify-content-between flex-fill">
                              <h5 className="text-uppercase">
                                Family Member & Dependents
                              </h5>
                              <div className="d-flex">
                                {/* <Link
                                  to="#"
                                  className="btn btn-icon btn-sm"
                                  data-bs-toggle="modal"
                                  data-inert={true}
                                  data-bs-target="#edit_familyinformation"
                                >
                                  <i className="ti ti-edit" />
                                </Link> */}
                                <Link
                                  to="#"
                                  className="d-flex align-items-center collapsed collapse-arrow"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#primaryBorderFour"
                                  aria-expanded="false"
                                  aria-controls="primaryBorderFour"
                                >
                                  <i className="ti ti-chevron-down fs-18" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderFour"
                          className="accordion-collapse collapse border-top accordion-body"
                          aria-labelledby="headingFour"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="d-flex flex-column gap-2">
                            {user?.familyMembers?.length > 0
                              ? user.familyMembers.map(
                                (member: any, index: number) => (
                                  <div key={index}>
                                    <div className="d-flex flex-column gap-1">
                                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                        <div
                                          className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                          style={{ minWidth: "160px" }}
                                        >
                                          Name:
                                        </div>
                                        <div
                                          className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                          style={{
                                            fontSize: "15px",
                                            flex: 1,
                                          }}
                                        >
                                          <span>
                                            {member.firstname}{" "}
                                            {member.lastname}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                        <div
                                          className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                          style={{ minWidth: "160px" }}
                                        >
                                          Relation:
                                        </div>
                                        <div
                                          className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                          style={{
                                            fontSize: "15px",
                                            flex: 1,
                                          }}
                                        >
                                          <span>{member.relation}</span>
                                        </div>
                                      </div>
                                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                        <div
                                          className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                          style={{ minWidth: "160px" }}
                                        >
                                          Gender:
                                        </div>
                                        <div
                                          className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                          style={{
                                            fontSize: "15px",
                                            flex: 1,
                                          }}
                                        >
                                          <span>
                                            {member.gender === 1 ||
                                              member.gender === "1"
                                              ? "Male"
                                              : member.gender === 2 ||
                                                member.gender === "2"
                                                ? "Female"
                                                : member.gender || "N/A"}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                        <div
                                          className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                          style={{ minWidth: "160px" }}
                                        >
                                          Nationality:
                                        </div>
                                        <div
                                          className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                          style={{
                                            fontSize: "15px",
                                            flex: 1,
                                          }}
                                        >
                                          <span>{member.nationality}</span>
                                        </div>
                                      </div>
                                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                        <div
                                          className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                          style={{ minWidth: "160px" }}
                                        >
                                          Dependency Type:
                                        </div>
                                        <div
                                          className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                          style={{
                                            fontSize: "15px",
                                            flex: 1,
                                          }}
                                        >
                                          <span>
                                            {getDependencyTypeDescription(
                                              member.dependencyType
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    {index <
                                      user.familyMembers.length - 1 && (
                                        <hr
                                          style={{
                                            margin: "12px 0",
                                            border: "1px solid #ccc",
                                            backgroundColor: "#ccc",
                                          }}
                                        />
                                      )}
                                  </div>
                                )
                              )
                              : null}
                          </div>
                        </div>
                      </div>
                      {/* Education Details */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingFive">
                          <div className="accordion-button">
                            <div className="d-flex align-items-center justify-content-between flex-fill">
                              <h5 className="text-uppercase">
                                Education Details
                              </h5>
                              <div className="d-flex">
                                {/* <Link
                                  to="#"
                                  className="btn btn-icon btn-sm"
                                  data-bs-toggle="modal"
                                  data-inert={true}
                                  data-bs-target="#edit_education"
                                >
                                  <i className="ti ti-edit" />
                                </Link> */}
                                <Link
                                  to="#"
                                  className="d-flex align-items-center collapsed collapse-arrow"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#primaryBorderFive"
                                  aria-expanded="false"
                                  aria-controls="primaryBorderFive"
                                >
                                  <i className="ti ti-chevron-down fs-18" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderFive"
                          className="accordion-collapse collapse border-top accordion-body"
                          aria-labelledby="headingFive"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="d-flex flex-column gap-2">
                            {user?.education?.length > 0
                              ? user.education.map((ed: any, index: number) => (
                                <div
                                  className="d-flex flex-column"
                                  key={index}
                                >
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Department:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.department}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Course:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>
                                        {ed.courseName2} / {ed.education_text}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Duration:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>
                                        {ed.dateOfJoining &&
                                          moment(ed.dateOfJoining).format(
                                            "DD-MM-YYYY"
                                          )}{" "}
                                        -{" "}
                                        {ed.daleofCompletion &&
                                          moment(ed.daleofCompletion).format(
                                            "DD-MM-YYYY"
                                          )}{" "}
                                        ({ed.durationofcourse})
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Passing Year:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.passingYear}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Percentage:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.percentage}%</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      CGPA:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.CGPA}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Division:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.division}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Education Training:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.education_training}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Course Appraisal:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.courseAppraisal}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Board University:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.boardUniversity}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                    <div
                                      className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                      style={{ minWidth: "160px" }}
                                    >
                                      Country:
                                    </div>
                                    <div
                                      className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                      style={{ fontSize: "15px", flex: 1 }}
                                    >
                                      <span>{ed.country}</span>
                                    </div>
                                  </div>
                                  {index < user.education.length - 1 && (
                                    <hr className="bg-dark my-1" />
                                  )}
                                </div>
                              ))
                              : null}
                          </div>
                        </div>
                      </div>
                      {/* Previous Employer */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingSix">
                          <div className="accordion-button collapsed">
                            <div className="d-flex align-items-center justify-content-between flex-fill">
                              <h5 className="text-uppercase">
                                Previous Employer OverView
                              </h5>
                              <div className="d-flex">
                                {/* <Link
                                  to="#"
                                  className="btn btn-icon btn-sm"
                                  data-bs-toggle="modal"
                                  data-inert={true}
                                  data-bs-target="#edit_experience"
                                >
                                  <i className="ti ti-edit" />
                                </Link> */}
                                <Link
                                  to="#"
                                  className="d-flex align-items-center collapsed collapse-arrow"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#primaryBorderSix"
                                  aria-expanded="false"
                                  aria-controls="primaryBorderSix"
                                >
                                  <i className="ti ti-chevron-down fs-18" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          id="primaryBorderSix"
                          className="accordion-collapse collapse border-top accordion-body"
                          aria-labelledby="headingSix"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="d-flex flex-column gap-2">
                            {user?.employers?.length > 0
                              ? user.employers.map(
                                (emp: any, index: number) => (
                                  <div
                                    className="d-flex flex-column"
                                    key={index}
                                  >
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Employer Name:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{emp.employerName}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Location:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>
                                          {emp.city}, {emp.country}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Duration:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>
                                          {moment(emp.startDate).format(
                                            "DD-MM-YYYY"
                                          )}{" "}
                                          to{" "}
                                          {moment(emp.endDate).format(
                                            "DD-MM-YYYY"
                                          )}{" "}
                                          {/* ({emp.durationMonths} months,{" "}
                                            {emp.durationYears} years) */}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Industry:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{emp.industry}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-baseline">
                                      <div
                                        className="text-uppercase fw-medium pe-sm-2 mb-1 mb-sm-0"
                                        style={{ minWidth: "160px" }}
                                      >
                                        Reason for leaving:
                                      </div>
                                      <div
                                        className="fw-medium text-black text-sm-end d-flex flex-row align-items-baseline justify-content-end gap-2"
                                        style={{ fontSize: "15px", flex: 1 }}
                                      >
                                        <span>{emp.reasonforLeaving}</span>
                                      </div>
                                    </div>
                                    {index < user.employers.length - 1 && (
                                      <hr className="bg-dark my-1" />
                                    )}
                                  </div>
                                )
                              )
                              : null}
                          </div>
                        </div>
                      </div>
                      {/* Police Verification */}
                      <div className="accordion-item">
                        <div className="accordion-header" id="headingSeven">
                          <Link to={all_routes.policeVerification} className="accordion-button collapsed text-decoration-none">
                            <div className="d-flex align-items-center justify-content-between flex-fill text-dark">
                              <h5 className="text-uppercase mb-0">
                                Police Verification Status
                              </h5>
                              <div className="d-flex text-muted">
                                <i className="ti ti-chevron-right fs-18" />
                              </div>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Know your CO */}
      <div className="modal fade" id="know_your_co">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Controlling Officer Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body d-flex flex-column gap-2">
              <div className="d-flex align-items-center justify-content-center">
                <img
                  alt="Manager Profile Pic"
                  className="rounded-circle"
                  style={{
                    border: "2px solid #c0282c",
                    padding: "2px",
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  src={
                    user?.details?.manager?.image
                      ? `data:image/jpg;base64,${user.details.manager?.image}`
                      : `assets/img/users/user-13.jpg`
                  }
                />
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Employee ID</div>
                <div className="text-dark">
                  {user?.details?.manager?.empId
                    ? parseInt(user.details.manager.empId, 10)
                    : "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Full Name</div>
                <div className="text-dark">
                  {user?.details?.manager?.firstname &&
                    user?.details?.manager?.lsastname
                    ? `${user.details.manager.firstname} ${user.details.manager.lsastname}`
                    : user?.details?.reportingManagerName || "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">{t("email")}</div>
                <div className="text-dark">
                  {user?.details?.manager?.email || "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Phone Number</div>
                <div className="text-dark">
                  {user?.details?.manager?.phone || "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Birthdate</div>
                <div className="text-dark">
                  {user?.details?.manager?.birthdate
                    ? moment(user.details.manager.birthdate).format(
                      "DD-MM-YYYY"
                    )
                    : "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Designation</div>
                <div className="text-dark">
                  {user?.details?.manager
                    ?.manager_sap_department_designation_wings
                    ?.designation_name || "N/A"}
                </div>
              </div>
              <div className="d-flex flex-row align-items-baseline justify-content-between">
                <div className="text-uppercase text-muted">Department</div>
                <div className="text-dark">
                  {user?.details?.manager
                    ?.manager_sap_department_designation_wings
                    ?.department_name || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Know your CO */}
      {/* Edit Contact Info */}
      <div className="modal fade" id="edit_contact">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Contact Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={
                  otpArray.every((digit) => digit !== "")
                    ? handleVerifyOTP
                    : handleUpdateContact
                }
              >
                <div className="form-group">
                  <label className="form-label text-dark">
                    Contact Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    value={phoneNumber}
                    maxLength={10}
                    disabled={updateContactStatus === 201}
                    name="phone"
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    required
                  />
                </div>
                {updateContactStatus === 201 ? (
                  <div className="form-group mt-2">
                    <label className="form-label text-dark">OTP</label>
                    <div className="text-center otp-input">
                      <div className="d-flex justify-content-start align-items-center gap-2 mb-2">
                        {otpArray.map((digit, index) => (
                          <input
                            key={index}
                            type="text"
                            maxLength={1}
                            value={digit}
                            className="form-input-otp text-center text-dark"
                            onChange={(e) =>
                              handleOtpChange(e.target.value, index)
                            }
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            ref={(el) => (inputRefs.current[index] = el)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-light border me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Edit Employee */}
      <div className="modal fade" id="edit_employee">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title me-2">Edit Employee</h4>
                <span>Employee ID : EMP -0024</span>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="contact-grids-tab">
                <ul className="nav nav-underline" id="myTab2" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="info-tab3"
                      data-bs-toggle="tab"
                      data-bs-target="#basic-info3"
                      type="button"
                      role="tab"
                      aria-selected="true"
                    >
                      Basic Information
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="address-tab3"
                      data-bs-toggle="tab"
                      data-bs-target="#address3"
                      type="button"
                      role="tab"
                      aria-selected="false"
                    >
                      Permissions
                    </button>
                  </li>
                </ul>
              </div>
              <div className="tab-content" id="myTabContent2">
                <div
                  className="tab-pane fade show active"
                  id="basic-info3"
                  role="tabpanel"
                  aria-labelledby="info-tab3"
                  tabIndex={0}
                >
                  <div className="modal-body pb-0 ">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {/* <ImageWithBasePath
                              src="assets/img/users/user-13.jpg"
                              alt="img"
                              className="rounded-circle"
                            /> */}
                          </div>
                          <div className="profile-upload">
                            <div className="mb-2">
                              <h6 className="mb-1">Upload Profile Image</h6>
                              <p className="fs-12">
                                Image should be below 4 mb
                              </p>
                            </div>
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn btn btn-sm btn-primary me-2">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  multiple
                                />
                              </div>
                              <Link to="#" className="btn btn-light btn-sm">
                                Cancel
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            First Name <span className="text-danger"> *</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue="Anthony"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="email"
                            className="form-control"
                            defaultValue="Lewis"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Employee ID <span className="text-danger"> *</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue="Emp-001"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Joining Date <span className="text-danger"> *</span>
                          </label>
                          <div className="input-icon-end position-relative">
                            <DatePicker
                              className="form-control datetimepicker"
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              getPopupContainer={getModalContainer}
                              placeholder="DD-MM-YYYY"
                            />
                            <span className="input-icon-addon">
                              <i className="ti ti-calendar text-gray-7" />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Username <span className="text-danger"> *</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue="Anthony"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Email <span className="text-danger"> *</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            defaultValue="anthony@example.com	"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3 ">
                          <label className="form-label">
                            Password <span className="text-danger"> *</span>
                          </label>
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.password
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                            />
                            <span
                              className={`ti toggle-passwords ${passwordVisibility.password
                                ? "ti-eye"
                                : "ti-eye-off"
                                }`}
                              onClick={() =>
                                togglePasswordVisibility("password")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3 ">
                          <label className="form-label">
                            Confirm Password{" "}
                            <span className="text-danger"> *</span>
                          </label>
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.confirmPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                            />
                            <span
                              className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                                }`}
                              onClick={() =>
                                togglePasswordVisibility("confirmPassword")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Phone Number <span className="text-danger"> *</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue="(123) 4567 890"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Company<span className="text-danger"> *</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue="Abac Company"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Department</label>
                          <CommonSelect
                            className="select"
                            options={departmentChoose}
                            defaultValue={departmentChoose[1]}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Designation</label>
                          <CommonSelect
                            className="select"
                            options={designationChoose}
                            defaultValue={designationChoose[1]}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">
                            About <span className="text-danger"> *</span>
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            defaultValue={
                              "As an award winning designer, I deliver exceptional quality work and bring value to your brand! With 10 years of experience and 350+ projects completed worldwide with satisfied customers, I developed the 360° brand approach, which helped me to create numerous brands that are relevant, meaningful and loved.\n\t\t\t\t\t\t\t\t\t\t\t\t\t"
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-light border me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-bs-dismiss="modal"
                      className="btn btn-primary"
                    >
                      Save{" "}
                    </button>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="address3"
                  role="tabpanel"
                  aria-labelledby="address-tab3"
                  tabIndex={0}
                >
                  <div className="modal-body">
                    <div className="card bg-light-500 shadow-none">
                      <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h6>Enable Options</h6>
                        <div className="d-flex align-items-center justify-content-end">
                          <div className="form-check form-switch me-2">
                            <label className="form-check-label mt-0">
                              <input
                                className="form-check-input me-2"
                                type="checkbox"
                                role="switch"
                              />
                              Enable all Module
                            </label>
                          </div>
                          <div className="form-check d-flex align-items-center">
                            <label className="form-check-label mt-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                defaultChecked
                              />
                              Select All
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="table-responsive border rounded">
                      <table className="table">
                        <tbody>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                    defaultChecked
                                  />
                                  Holidays
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    defaultChecked
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    defaultChecked
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Leaves
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Clients
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Projects
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Tasks
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Chats
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                    defaultChecked
                                  />
                                  Assets
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    defaultChecked
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    defaultChecked
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch me-2">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    role="switch"
                                  />
                                  Timing Sheets
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Read
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Write
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Create
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Delete
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Import
                                </label>
                              </div>
                            </td>
                            <td>
                              <div className="form-check d-flex align-items-center">
                                <label className="form-check-label mt-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                  />
                                  Export
                                </label>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-light border me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      data-bs-toggle="modal"
                      data-inert={true}
                      data-bs-target="#success_modal"
                    >
                      Save{" "}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Employee */}
      {/* Edit Personal */}
      <div className="modal fade" id="edit_personal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Personal Info</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Passport No <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Passport Expiry Date{" "}
                        <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Nationality <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Religion</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Marital status <span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={martialstatus}
                        defaultValue={martialstatus[0]}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Employment spouse</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">No. of children</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Personal */}
      {/* Edit Emergency Contact */}
      <div className="modal fade" id="edit_emergency" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Emergency Contact Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            {/* ✅ Hooked up to our submit function */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Enter Name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      maxLength={10}
                      className="form-control"
                      placeholder="Enter Phone Number"
                      value={form.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email ID</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter Email"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Relation</label>
                    <select
                      name="relation"
                      className="form-select"
                      value={form.relation}
                      onChange={handleChange}
                    >
                      <option value="">Select Relation</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Sister">Sister</option>
                      <option value="Brother">Brother</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      name="address"
                      className="form-control"
                      placeholder="Enter Address"
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-danger">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* /Edit Emergency Contact */}
      {/* Add Addresses */}
      <div className="modal fade" id="edit_emergency" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Emergency Contact Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Enter Name"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      maxLength={10}
                      value={form.phone}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Enter Phone Number"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email ID</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Enter Email ID"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Relation</label>
                    <select
                      name="relation"
                      value={form.relation}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select Relation</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Sister">Sister</option>
                      <option value="Brother">Brother</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Enter Address"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  aria-label="Close"
                  type="submit"
                  className="btn btn-danger"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Add Addresses */}
      {/* Edit Address */}
      <div className="modal fade" id="edit_address">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Address Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const addressId = editAddressData?.id || null;
                const form = e.target as HTMLFormElement;
                const formData = new FormData(form);
                const addressDetails = Object.fromEntries(formData.entries());
                if (addressId) addressDetails.id = addressId;
                updateAddressData(addressDetails);
                fetchRecords();
                form.reset();
              }}
            >
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    {/* <div className="mb-2">
                      <label className="form-label">
                        Address Type
                        <span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={addressType}
                        defaultValue={addressType[1]}
                        name="addressType"
                      />
                    </div> */}
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex flex-row align-items-baseline gap-2">
                        <input
                          type="text"
                          className="form-control"
                          name="careOf"
                          placeholder="Care of"
                          defaultValue={editAddressData?.careOf || ""}
                        />
                        <input
                          type="text"
                          className="form-control"
                          name="house_number"
                          placeholder="House Number"
                          defaultValue={editAddressData?.house_number || ""}
                        />
                        <input
                          type="text"
                          className="form-control"
                          name="streetHouse"
                          placeholder="House / Street"
                          defaultValue={editAddressData?.streetHouse || ""}
                        />
                      </div>
                      <div className="d-flex flex-row align-items-baseline gap-2">
                        <input
                          type="text"
                          className="form-control"
                          name="address_line_2"
                          placeholder="Address Line 2"
                          defaultValue={editAddressData?.address_line_2 || ""}
                        />
                        <input
                          type="text"
                          className="form-control"
                          name="telephoneNo"
                          placeholder="Telephone No"
                          defaultValue={editAddressData?.telephoneNo || ""}
                        />
                      </div>
                      <div className="d-flex flex-row align-items-baseline gap-2">
                        <input
                          type="text"
                          className="form-control"
                          name="city"
                          placeholder="City"
                          defaultValue={editAddressData?.city || ""}
                        />
                        <input
                          type="text"
                          className="form-control"
                          name="postalCode"
                          placeholder="Postal Code"
                          defaultValue={editAddressData?.postalCode || ""}
                        />
                        {/* <input
                          type="text"
                          className="form-control"
                          name="district"
                          placeholder="District"
                        /> */}
                        <input
                          type="text"
                          className="form-control"
                          name="country"
                          placeholder="Country"
                          defaultValue={editAddressData?.country || ""}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Edit Address */}
      {/* Edit Bank */}
      <div className="modal fade" id="edit_bank">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Bank Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Bank Details <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Bank account No </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">IFSC Code</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Branch Address</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Bank */}
      {/* Add Family */}
      <div className="modal fade" id="edit_familyinformation">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Family Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Name <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Relationship </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Phone </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Passport Expiry Date{" "}
                        <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Family */}
      {/* Add Education */}
      <div className="modal fade" id="edit_education">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Education Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Institution Name <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Course <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Start Date <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        End Date <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Education */}
      {/* Add Experience */}
      <div className="modal fade" id="edit_experience">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Company Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Previous Company Name{" "}
                        <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Designation <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Start Date <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        End Date <span className="text-danger"> *</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-check-label d-flex align-items-center mt-0">
                        <input
                          className="form-check-input mt-0 me-2"
                          type="checkbox"
                          defaultChecked
                        />
                        <span className="text-dark">
                          Check if you working present
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Experience */}
      {/* Add Employee Success */}
      <div className="modal fade" id="success_modal" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center p-3">
                <span className="avatar avatar-lg avatar-rounded bg-success mb-3">
                  <i className="ti ti-check fs-24" />
                </span>
                <h5 className="mb-2">Employee Added Successfully</h5>
                <p className="mb-3">
                  Stephan Peralt has been added with Client ID :{" "}
                  <span className="text-primary">#EMP - 0001</span>
                </p>
                <div>
                  <div className="row g-2">
                    <div className="col-6">
                      <Link
                        to={all_routes.myProfile}
                        className="btn btn-dark w-100"
                      >
                        Back to List
                      </Link>
                    </div>
                    <div className="col-6">
                      <Link
                        to={all_routes.myProfile}
                        className="btn btn-primary w-100"
                      >
                        Detail Page
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Client Success */}
      {/* Add Statuorty */}
      <div className="modal fade" id="add_bank_satutory">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Bank &amp; Statutory</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="border-bottom mb-4">
                  <h5 className="mb-3">Basic Salary Information</h5>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">
                          Salary basis <span className="text-danger"> *</span>
                        </label>
                        <CommonSelect
                          className="select"
                          options={salaryChoose}
                          defaultValue={salaryChoose[0]}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Salary basis</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue="$"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Payment type</label>
                        <CommonSelect
                          className="select"
                          options={paymenttype}
                          defaultValue={paymenttype[0]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-bottom mb-4">
                  <h5 className="mb-3">PF Information</h5>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">
                          PF contribution{" "}
                          <span className="text-danger"> *</span>
                        </label>
                        <CommonSelect
                          className="select"
                          options={pfcontribution}
                          defaultValue={pfcontribution[0]}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">PF No</label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Employee PF rate</label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Additional rate</label>
                        <CommonSelect
                          className="select"
                          options={additionalrate}
                          defaultValue={additionalrate[0]}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Total rate</label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                  </div>
                </div>
                <h5 className="mb-3">ESI Information</h5>
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">
                        ESI contribution
                        <span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={esi}
                        defaultValue={esi[0]}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">ESI Number</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">
                        Employee ESI rate
                        <span className="text-danger"> *</span>
                      </label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Additional rate</label>
                      <CommonSelect
                        className="select"
                        options={additionalrate}
                        defaultValue={additionalrate[0]}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Total rate</label>
                      <input type="text" className="form-control" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Statuorty */}
      {/* Asset Information */}
      <div className="modal fade" id="asset_info">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Asset Information</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-light p-3 rounded d-flex align-items-center mb-3">
                <span className="avatar avatar-lg flex-shrink-0 me-2">
                  <i className="ti ti-device-laptop" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                </span>
                <div>
                  <h6>Dell Laptop - #343556656</h6>
                  <p className="fs-13">
                    <span className="text-primary">AST - 001 </span>
                    <i className="ti ti-point-filled text-primary" /> Assigned
                    on 22 Nov, 2022 10:32AM
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Type</p>
                    <p className="text-gray-9">Laptop</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Brand</p>
                    <p className="text-gray-9">Dell</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Category</p>
                    <p className="text-gray-9">Computer</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Serial No</p>
                    <p className="text-gray-9">3647952145678</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Cost</p>
                    <p className="text-gray-9">$800</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Vendor</p>
                    <p className="text-gray-9">Compusoft Systems Ltd.,</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Warranty</p>
                    <p className="text-gray-9">12 Jan 2022 - 12 Jan 2026</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <p className="fs-13 mb-0">Location</p>
                    <p className="text-gray-9">46 Laurel Lane, TX 79701</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="fs-13 mb-2">Asset Images</p>
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-md rounded me-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f5f5f5' }}>
                    <i className="ti ti-device-laptop" style={{ fontSize: '32px', color: '#6c757d' }}></i>
                  </div>
                  <div className="avatar avatar-md rounded me-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f5f5f5' }}>
                    <i className="ti ti-device-laptop" style={{ fontSize: '32px', color: '#6c757d' }}></i>
                  </div>
                  <div className="avatar avatar-md rounded d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f5f5f5' }}>
                    <i className="ti ti-device-laptop" style={{ fontSize: '32px', color: '#6c757d' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Asset Information */}
      {/* Refuse */}
      <div className="modal fade" id="refuse_msg">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Raise Issue</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Description<span className="text-danger"> *</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={4}
                        defaultValue={""}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Refuse */}



      {/* Create New Request Modal */}
      {showNewRequest && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">UPLOAD OR CHANGE OFFICIAL PHOTO</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => {
                    setShowNewRequest(false);
                    setSelectedFile(null);
                    setUploadedPhotoPreview("");
                  }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p>
                    Upload Passport size studio quality photo (.jpg or .jpeg
                    file type ONLY)
                  </p>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <input
                      type="file"
                      id="photoUpload"
                      className="d-none"
                      accept=".jpg,.jpeg"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="photoUpload" className="btn btn-danger">
                      Choose File
                    </label>
                    <span className="text-muted">
                      {selectedFile ? selectedFile.name : "No File Chosen"}
                    </span>
                    <button
                      className="btn btn-danger"
                      disabled={!selectedFile}
                      onClick={handleUploadPhoto}
                    >
                      Upload
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-uppercase">
                    PREVIOUS INCLUSION REQUEST
                  </h6>
                  <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className="table table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th>Uploaded Photo</th>
                          <th>Reference No</th>
                          <th>Date of Upload</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousRequests.map((request, index) => {
                          const imgData =
                            request?.FileData ||
                            request?.fileData ||
                            request?.image ||
                            request?.file_data ||
                            request?.FileDataBase64 ||
                            request?.photo ||
                            request?.documentUrl ||
                            request?.url;
                          const currentActiveImg =
                            user?.details?.image ||
                            user?.sap_employee_images?.image ||
                            user?.image;

                          const imgSrc = imgData
                            ? imgData.startsWith("data:") || imgData.startsWith("http")
                              ? imgData
                              : `data:image/jpeg;base64,${imgData}`
                            : currentActiveImg
                            ? currentActiveImg.startsWith("data:") || currentActiveImg.startsWith("http")
                              ? currentActiveImg
                              : `data:image/jpeg;base64,${currentActiveImg}`
                            : `assets/img/users/user-13.jpg`;

                          const statusMap: Record<string, { label: string; badge: string }> = {
                            N: { label: "New", badge: "bg-info" },
                            T: { label: "To Be Approved", badge: "bg-warning text-dark" },
                            P: { label: "Partially Approved", badge: "bg-warning text-dark" },
                            A: { label: "Approved", badge: "bg-success" },
                            R: { label: "Rejected", badge: "bg-danger" },
                            D: { label: "Processing Complete", badge: "bg-success" },
                            X: { label: "Deleted", badge: "bg-secondary" },
                            B: { label: "To Be Processed", badge: "bg-warning text-dark" },
                            C: { label: "Closed", badge: "bg-secondary" },
                            K: { label: "Cancelled", badge: "bg-danger" },
                            S: { label: "Settled", badge: "bg-success" },
                          };

                          const statusInfo = statusMap[request?.RequestStatus || request?.status] || {
                            label: request?.RequestStatus || request?.status || "N/A",
                            badge: "bg-secondary",
                          };

                          return (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    src={imgSrc}
                                    alt="Profile Photo"
                                    className="rounded-circle me-2"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              </td>
                              <td>{request?.ReferenceNumber || request?.referenceNo || "N/A"}</td>
                              <td>{request?.CreatedOn || request?.dateOfUpload || request?.date || "N/A"}</td>
                              <td>
                                <span className={`badge ${statusInfo.badge}`}>
                                  {statusInfo.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    setShowNewRequest(false);
                    setSelectedFile(null);
                    setUploadedPhotoPreview("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Address Modal */}
      {showTempAddressModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  {tempAddressForm.addressType
                    ? tempAddressForm.addressType.toUpperCase()
                    : "ADD ADDRESS"}
                </h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => {
                    setShowTempAddressModal(false);
                    resetTempAddressForm();
                  }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleTempAddressSubmit}>
                  {/* Address Type */}
                  <div className="mb-3">
                    <label className="form-label">
                      Address Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${addressErrors.addressType ? "is-invalid" : ""}`}
                      name="addressType"
                      value={tempAddressForm.addressType}
                      onChange={handleTempAddressChange}
                    >
                      <option value="">Select Address Type</option>
                      {availableAddressTypes.map((t) => (
                        <option key={t.type_id || t.name} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    {addressErrors.addressType && (
                      <div className="invalid-feedback">{addressErrors.addressType}</div>
                    )}
                  </div>

                  {/* Country */}
                  <div className="mb-3">
                    <label className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.country ? "is-invalid" : ""}`}
                      name="country"
                      value={tempAddressForm.country}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.country && (
                      <div className="invalid-feedback">{addressErrors.country}</div>
                    )}
                  </div>

                  {/* Address Section */}
                  <hr className="my-3 text-muted" />
                  <h6 className="text-uppercase fw-bold text-dark pb-1 border-bottom mb-3">
                    ADDRESS
                  </h6>

                  {/* Care Of */}
                  <div className="mb-3">
                    <label className="form-label">
                      Care Of <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.careOf ? "is-invalid" : ""}`}
                      name="careOf"
                      placeholder="Enter Care Of"
                      value={tempAddressForm.careOf}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.careOf && (
                      <div className="invalid-feedback">{addressErrors.careOf}</div>
                    )}
                  </div>

                  {/* House Number */}
                  <div className="mb-3">
                    <label className="form-label">
                      House Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.houseNumber ? "is-invalid" : ""}`}
                      name="houseNumber"
                      placeholder="Enter House No."
                      value={tempAddressForm.houseNumber}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.houseNumber && (
                      <div className="invalid-feedback">{addressErrors.houseNumber}</div>
                    )}
                  </div>

                  {/* Street */}
                  <div className="mb-3">
                    <label className="form-label">
                      Street <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.street ? "is-invalid" : ""}`}
                      name="street"
                      placeholder="Enter street"
                      value={tempAddressForm.street}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.street && (
                      <div className="invalid-feedback">{addressErrors.street}</div>
                    )}
                  </div>

                  {/* Address */}
                  <div className="mb-3">
                    <label className="form-label">
                      Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.address ? "is-invalid" : ""}`}
                      name="address"
                      placeholder="Enter address"
                      value={tempAddressForm.address}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.address && (
                      <div className="invalid-feedback">{addressErrors.address}</div>
                    )}
                  </div>

                  {/* City */}
                  <div className="mb-3">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${addressErrors.city ? "is-invalid" : ""}`}
                      name="city"
                      placeholder="Enter city"
                      value={tempAddressForm.city}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.city && (
                      <div className="invalid-feedback">{addressErrors.city}</div>
                    )}
                  </div>

                  {/* State and Postal Code */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">
                        State <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${addressErrors.state ? "is-invalid" : ""}`}
                        name="state"
                        value={tempAddressForm.state}
                        onChange={handleTempAddressChange}
                        disabled={loadingRegions}
                      >
                        <option value="">
                          {loadingRegions ? "Loading states..." : "Select State"}
                        </option>
                        {tempAddressForm.state &&
                          !regions.some((r) => r.region_name === tempAddressForm.state) && (
                            <option value={tempAddressForm.state}>
                              {tempAddressForm.state}
                            </option>
                          )}
                        {regions.map((region, idx) => (
                          <option
                            key={region.region_id || `${region.region_name}-${idx}`}
                            value={region.region_name}
                          >
                            {region.region_name}
                          </option>
                        ))}
                      </select>
                      {addressErrors.state && (
                        <div className="invalid-feedback">{addressErrors.state}</div>
                      )}
                    </div>
                    <div className="col-6">
                      <label className="form-label">
                        Postal Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        className={`form-control ${addressErrors.postalCode ? "is-invalid" : ""}`}
                        name="postalCode"
                        placeholder="Enter postal code"
                        value={tempAddressForm.postalCode}
                        onChange={handleTempAddressChange}
                      />
                      {addressErrors.postalCode && (
                        <div className="invalid-feedback">{addressErrors.postalCode}</div>
                      )}
                    </div>
                  </div>

                  {/* Communication Section */}
                  <hr className="my-3 text-muted" />
                  <h6 className="text-uppercase fw-bold text-dark pb-1 border-bottom mb-3">
                    COMMUNICATION
                  </h6>

                  {/* Telephone */}
                  <div className="mb-3">
                    <label className="form-label">
                      Telephone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      maxLength={10}
                      className={`form-control ${addressErrors.telephone ? "is-invalid" : ""}`}
                      name="telephone"
                      placeholder="Enter phone number"
                      value={tempAddressForm.telephone}
                      onChange={handleTempAddressChange}
                    />
                    {addressErrors.telephone && (
                      <div className="invalid-feedback">{addressErrors.telephone}</div>
                    )}
                  </div>

                  {/* Validity Section */}
                  <hr className="my-3 text-muted" />
                  <h6 className="text-uppercase fw-bold text-dark pb-1 border-bottom mb-3">
                    VALIDITY
                  </h6>
                  <div className="mb-3">
                    {/* Option 1: Valid as of today */}
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="validOption"
                        id="validToday"
                        checked={tempAddressForm.validOption === "today"}
                        onChange={() => handleValidOptionChange("today")}
                      />
                      <label className="form-check-label text-dark fw-medium" htmlFor="validToday">
                        Valid as of today
                      </label>
                    </div>

                    {/* Option 2: Valid From */}
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="validOption"
                        id="validFrom"
                        checked={tempAddressForm.validOption === "from"}
                        onChange={() => handleValidOptionChange("from")}
                      />
                      <label className="form-check-label text-dark fw-medium" htmlFor="validFrom">
                        Valid From
                      </label>
                    </div>

                    {/* Option 2: Nested From Date Input */}
                    {tempAddressForm.validOption === "from" && (
                      <div className="ms-4 mb-3 ps-1">
                        <label className="form-label text-dark">
                          From Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className={`form-control ${addressErrors.validFrom ? "is-invalid" : ""}`}
                          name="validFrom"
                          value={tempAddressForm.validFrom}
                          onChange={handleTempAddressChange}
                        />
                        {addressErrors.validFrom && (
                          <div className="invalid-feedback">{addressErrors.validFrom}</div>
                        )}
                      </div>
                    )}

                    {/* Option 3: Valid From/To */}
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="validOption"
                        id="validFromTo"
                        checked={tempAddressForm.validOption === "fromTo"}
                        onChange={() => handleValidOptionChange("fromTo")}
                      />
                      <label className="form-check-label text-dark fw-medium" htmlFor="validFromTo">
                        Valid From/To
                      </label>
                    </div>

                    {/* Option 3: Nested Date Range Inputs */}
                    {tempAddressForm.validOption === "fromTo" && (
                      <div className="ms-4 mb-3 ps-1">
                        <div className="row">
                          <div className="col-6">
                            <label className="form-label text-dark">
                              From Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control ${addressErrors.validFrom ? "is-invalid" : ""}`}
                              name="validFrom"
                              value={tempAddressForm.validFrom}
                              onChange={handleTempAddressChange}
                            />
                            {addressErrors.validFrom && (
                              <div className="invalid-feedback">{addressErrors.validFrom}</div>
                            )}
                          </div>
                          <div className="col-6">
                            <label className="form-label text-dark">
                              To Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control ${addressErrors.validTo ? "is-invalid" : ""}`}
                              name="validTo"
                              value={tempAddressForm.validTo}
                              onChange={handleTempAddressChange}
                            />
                            {addressErrors.validTo && (
                              <div className="invalid-feedback">{addressErrors.validTo}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    setShowTempAddressModal(false);
                    resetTempAddressForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleTempAddressSubmit}
                >
                  SUBMIT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyProfile;
